import type { ShiftDropoff } from "@domain/CRM/dropoff";
import type { Job } from "@domain/CRM/job";
import { isBacklogJob, isUnsampledScheduledJob, withRereadJobs } from "@domain/CRM/job";
import type { Shift } from "@domain/CRM/shift";
import { schedulingShiftCutoffDate, schedulingStopsCutoffDate } from "@domain/CRM/shift";
import type { ISchedulingBoardCache } from "@ports/shifts/ISchedulingBoardCache";
import type { ISchedulingBoardReads } from "@ports/shifts/ISchedulingBoardReads";

/** The Scheduling board's read side: its lists, and the caches behind them. */
export interface SchedulingBoardDeps {
	boardReads: ISchedulingBoardReads;
	boardCache: ISchedulingBoardCache;
}

/** What a save's targeted re-read brings back. */
export interface AfterSaveReread {
	shifts: Shift[];
	/** Null when the save changed no stop: the board's stops still stand. */
	stops: ShiftDropoff[] | null;
	/** The touched jobs as they are now; a touched id with no record is absent. */
	jobs: Job[];
}

/** One list change per cached job list: each swaps the re-read jobs in (see `withRereadJobs`). */
export interface RereadJobChanges {
	backlog: (jobs: readonly Job[]) => Job[];
	scheduled: (jobs: readonly Job[]) => Job[];
}

/** Each look reaches this far back past the last one's start, so a job written while that look
 *  was in flight, or a few minutes off this machine's clock, is not missed. Seeing a job twice
 *  is harmless: it is swapped in again. */
const INCOMING_OVERLAP_MS = 2 * 60 * 1000;

/**
 * After a save: re-read only what it can have changed — the shift list (one short read), the
 * stops if it touched any, and the jobs it wrote or moved, by id — rather than both whole job
 * lists. The shift and stop caches are dropped first so the reads refetch.
 */
export async function reloadAfterSave(
	deps: SchedulingBoardDeps,
	{ touchedJobIds, stopsChanged }: { touchedJobIds: readonly string[]; stopsChanged: boolean },
): Promise<AfterSaveReread> {
	deps.boardCache.invalidate("shifts");
	if (stopsChanged) deps.boardCache.invalidate("stops");
	const [shifts, stops, jobs] = await Promise.all([
		deps.boardReads.shiftsSince(schedulingShiftCutoffDate()),
		stopsChanged ? deps.boardReads.stopsSince(schedulingStopsCutoffDate()) : null,
		touchedJobIds.length > 0 ? deps.boardReads.jobsByIds([...touchedJobIds]) : Promise.resolve([] as Job[]),
	]);
	return { shifts, stops, jobs };
}

/** Saved assignments move jobs out of the backlog, so after a save that could not re-read what it
 *  touched, every list it can have changed is refetched, not read from the stale caches. */
export function dropBoardCaches(deps: SchedulingBoardDeps): void {
	deps.boardCache.invalidate("shifts");
	deps.boardCache.invalidate("stops");
	deps.boardCache.invalidate("backlog");
}

/**
 * Swap re-read jobs into both cached job lists — each job into the list whose read would return
 * it now, or out of both — and return the same changes, for the board to apply to its own lists.
 * Every other record keeps its identity.
 */
export function storeRereadJobs(
	deps: SchedulingBoardDeps,
	reread: readonly Job[],
	rereadIds: readonly string[],
	{ showTestJobs }: { showTestJobs: boolean },
): RereadJobChanges {
	const backlog = (list: readonly Job[]) =>
		withRereadJobs(list, reread, rereadIds, (job) => isBacklogJob(job, showTestJobs));
	const scheduled = (list: readonly Job[]) => withRereadJobs(list, reread, rereadIds, isUnsampledScheduledJob);
	deps.boardCache.updateJobs("backlog", backlog);
	deps.boardCache.updateJobs("scheduledJobs", scheduled);
	return { backlog, scheduled };
}

/** Where the board's first look for incoming jobs starts. Lists taken from the cache can be as old
 *  as the cache lets them be (`listsMaxAgeMs`), so it reaches back that far; a fresh load starts
 *  covering from now. */
export function firstIncomingLookFrom(
	deps: SchedulingBoardDeps,
	{ now, listsFromCache }: { now: number; listsFromCache: boolean },
): number {
	const listsAge = listsFromCache ? deps.boardCache.listsMaxAgeMs : 0;
	return now - listsAge - INCOMING_OVERLAP_MS;
}

/** One look for incoming jobs: those modified since `from`, and where the next look starts. */
export async function readIncomingJobs(
	deps: SchedulingBoardDeps,
	{ from, startedAt }: { from: number; startedAt: number },
): Promise<{ jobs: Job[]; nextFrom: number }> {
	const jobs = await deps.boardReads.jobsModifiedSince(new Date(from));
	return { jobs, nextFrom: startedAt - INCOMING_OVERLAP_MS };
}
